import React, { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useLocation } from 'react-router-dom';

interface PromiseRejectionHandlerProps {
  children: React.ReactElement;
}

export function PromiseRejectionHandler({
  children,
}: PromiseRejectionHandlerProps): React.ReactElement {
  const [hasError, setError] = useState(false);

  const promiseRejectionHandler = (_event: PromiseRejectionEvent) => {
    setError(true);
  };

  useEffect(() => {
    window.addEventListener('unhandledrejection', promiseRejectionHandler);
    return () => {
      window.removeEventListener('unhandledrejection', promiseRejectionHandler);
    };
  }, []);

  useEffect(() => {
    if (hasError) {
      setError(false);
      throw new Error('ErrorWrapper: Unhandled Rejection Error!');
    }
  }, [hasError]);

  return children;
}

interface ErrorWrapperProps {
  redirectElement: React.ReactElement;
  children: React.ReactElement;
}

export function ErrorWrapper(props: Readonly<ErrorWrapperProps>): React.ReactElement {
  const location = useLocation();
  function ErrorFallback() {
    return props.redirectElement;
  }

  return (
    <ErrorBoundary fallbackRender={ErrorFallback} resetKeys={[location.pathname]}>
      <PromiseRejectionHandler>{props.children}</PromiseRejectionHandler>
    </ErrorBoundary>
  );
}

export function throwWrapper(error: Error, handleError: (error: unknown) => void) {
  handleError(error);
  throw error;
}
